import { useMemo } from 'react';
import { useIsFocused } from '@react-navigation/native';
import { type Asset } from 'contentful';

import { useContentfulContentTypeEntries } from '@order/Contentful';
import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsContentData = () => {
  const isScreenFocused = useIsFocused();

  // ─── Flags ───────────────────────────────────────────────────────────

  const isProductDetailsPageEnabled = useFeatureFlag(
    'CELS-4497-order-product-details-page-enabled',
  );

  // ─── Remote Data ─────────────────────────────────────────────────────

  const { data, fetching } = useContentfulContentTypeEntries<ContentEntry>({
    contentType: 'productDetails',
    limit: 500,
    pause: !isProductDetailsPageEnabled || !isScreenFocused,
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const productDetailsContentData = useMemo(() => {
    if (!data) return;

    return new Map(data.map((item) => [item.fields?.baseProductSlug, item]));
  }, [data]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    productDetailsContentData,
    isFetchingProductDetailsContentData: fetching,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ContentEntry = {
  baseProductSlug: string;
  cardHoverImage: Asset | undefined;
  cardHoverStaticImage: Asset | undefined;
  carousel?: { fields: { imageVideo: Asset; staticImage?: Asset } }[];
};
